/**
 * DEL-B — shared delete toast / recently-deleted display helpers.
 */

import type { ReactNode } from "react";
import type { RecentlyDeletedItem } from "@/lib/styleCurationApi";

export function formatPurgeDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function kindDisplayLabel(kind: string): string {
  switch (kind) {
    case "style":
      return "Style";
    case "version":
      return "Version";
    case "colorway":
      return "Colorway";
    case "multi_view":
      return "Multi-View";
    case "orbit":
      return "Orbit";
    case "3d":
      return "3D";
    default:
      return kind;
  }
}

export function recentlyDeletedName(item: RecentlyDeletedItem): string {
  const styleName =
    typeof item.style_name === "string" && item.style_name.trim()
      ? item.style_name.trim()
      : "Style";
  if (item.kind === "style") return styleName;
  const label =
    typeof item.label === "string" && item.label.trim()
      ? item.label.trim()
      : kindDisplayLabel(String(item.kind));
  return `${styleName} · ${label}`;
}

export function alsoLabelsLine(item: RecentlyDeletedItem): string | null {
  const also = Array.isArray(item.also) ? item.also : [];
  const labels = also
    .map((a) => {
      if (!a || typeof a !== "object") return null;
      const row = a as { label?: unknown; kind?: unknown };
      if (typeof row.label === "string" && row.label.trim()) return row.label.trim();
      if (typeof row.kind === "string") return kindDisplayLabel(row.kind);
      return null;
    })
    .filter((x): x is string => !!x);
  if (!labels.length) return null;
  return labels.join(", ");
}

export function alsoKindsShort(item: RecentlyDeletedItem): string | null {
  const also = Array.isArray(item.also) ? item.also : [];
  const kinds = [
    ...new Set(
      also
        .map((a) =>
          a && typeof a === "object" && typeof (a as { kind?: unknown }).kind === "string"
            ? kindDisplayLabel(String((a as { kind: string }).kind))
            : null,
        )
        .filter((x): x is string => !!x),
    ),
  ];
  if (!kinds.length) return null;
  return kinds.join(", ");
}

export function deletedToastTitle(
  label: string,
  newMasterJobId: string | null,
): string {
  return newMasterJobId
    ? `${label} moved to Recently deleted · new Master set`
    : `${label} moved to Recently deleted`;
}

/** Build delete dialog title for a job root. */
export function deleteJobTitle(opts: {
  kind: "version" | "colorway" | "multi_view" | "orbit" | "3d" | string;
  label: string;
  isMaster?: boolean;
}): string {
  const { kind, label, isMaster } = opts;
  if (kind === "version") {
    const v = label.startsWith("V") ? label : `V${label}`;
    return isMaster ? `Delete ${v} (Master)?` : `Delete ${v}?`;
  }
  if (kind === "colorway") return `Delete colorway ${label}?`;
  if (kind === "multi_view") return `Delete Multi-View of ${label}?`;
  if (kind === "orbit") return `Delete Orbit of ${label}?`;
  return `Delete ${label}?`;
}

export type UndoToastActionFactory = (opts: {
  onUndo: () => void;
}) => ReactNode;
